import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useViewAs } from '../context/ViewAsContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  FileText,
  Clock,
  CheckCircle,
  TrendingUp,
  Calendar,
  AlertCircle,
  ArrowUpRight,
  Activity,
  Download,
  RefreshCw
} from 'lucide-react';
import { referralsAPI, statsAPI } from '../services/api';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const Dashboard = () => {
  const { user } = useAuth();
  const { getEffectiveRole } = useViewAs();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_referrals: 0,
    pending_referrals: 0,
    scheduled_referrals: 0,
    completed_referrals: 0,
    monthly_growth: 0
  });
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshIntervalRef = useRef(null);

  const effectiveRole = getEffectiveRole(user?.role);

  // Mock data for charts - in production, fetch from API
  const [userGrowthData, setUserGrowthData] = useState([
    { month: 'Jan', users: 45, newUsers: 12 },
    { month: 'Feb', users: 62, newUsers: 17 },
    { month: 'Mar', users: 85, newUsers: 23 },
    { month: 'Apr', users: 110, newUsers: 25 },
    { month: 'May', users: 142, newUsers: 32 },
    { month: 'Jun', users: 178, newUsers: 36 },
    { month: 'Jul', users: 215, newUsers: 37 },
    { month: 'Aug', users: 256, newUsers: 41 },
    { month: 'Sep', users: 298, newUsers: 42 },
    { month: 'Oct', users: 342, newUsers: 44 },
    { month: 'Nov', users: 389, newUsers: 47 }
  ]);

  const [referralTrendsData, setReferralTrendsData] = useState([
    { week: 'Week 1', pending: 12, scheduled: 8, completed: 15, total: 35 },
    { week: 'Week 2', pending: 15, scheduled: 10, completed: 18, total: 43 },
    { week: 'Week 3', pending: 10, scheduled: 12, completed: 20, total: 42 },
    { week: 'Week 4', pending: 18, scheduled: 15, completed: 22, total: 55 },
    { week: 'Week 5', pending: 14, scheduled: 11, completed: 25, total: 50 },
    { week: 'Week 6', pending: 16, scheduled: 14, completed: 28, total: 58 }
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Fetch stats for admin and superadmin
        if (effectiveRole === 'admin' || effectiveRole === 'superadmin') {
          try {
            const statsResponse = await statsAPI.getStats();
            if (statsResponse.success) {
              setStats(statsResponse.stats);
            }
          } catch (error) {
            console.error('Error fetching stats:', error);
          }
        }

        // Fetch referrals
        try {
          const referralsResponse = await referralsAPI.getAll();
          if (referralsResponse.success) {
            setReferrals(referralsResponse.referrals);
          }
        } catch (error) {
          console.error('Error fetching referrals:', error);
        }
        
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, effectiveRole]);

  // Auto-refresh setup
  useEffect(() => {
    if (autoRefresh && (effectiveRole === 'admin' || effectiveRole === 'superadmin')) {
      refreshIntervalRef.current = setInterval(() => {
        fetchReferralsData();
      }, 30000); // Refresh every 30 seconds

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, effectiveRole]);

  const fetchReferralsData = async () => {
    try {
      const referralsResponse = await referralsAPI.getAll();
      if (referralsResponse.success) {
        setReferrals(referralsResponse.referrals);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
    }
  };

  const handleManualRefresh = () => {
    setLoading(true);
    fetchReferralsData().finally(() => setLoading(false));
  };

  const exportToCSV = (dataType) => {
    let csvContent = '';
    let filename = '';

    if (dataType === 'userGrowth') {
      csvContent = 'Month,Total Users,New Users\n';
      userGrowthData.forEach(row => {
        csvContent += `${row.month},${row.users},${row.newUsers}\n`;
      });
      filename = 'user_growth_data.csv';
    } else if (dataType === 'referralTrends') {
      csvContent = 'Week,Pending,Scheduled,Completed,Total\n';
      referralTrendsData.forEach(row => {
        csvContent += `${row.week},${row.pending},${row.scheduled},${row.completed},${row.total}\n`;
      });
      filename = 'referral_trends_data.csv';
    } else if (dataType === 'allReferrals') {
      csvContent = 'ID,Patient Name,Procedure,Status,Urgency,Referring Doctor,Date\n';
      referrals.forEach(ref => {
        csvContent += `${ref.id || ''},"${ref.patient_name || ref.patientName || ''}","${ref.procedure || ''}",${ref.status || ''},${ref.urgency || ''},"${ref.referring_doctor_name || ref.referringDoctor || ''}","${ref.created_at || new Date().toISOString()}"\n`;
      });
      filename = 'all_referrals_data.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      routine: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[urgency] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const renderAdminDashboard = () => (
    <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-l-4 border-l-[#D4AF37] hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center justify-between">
              <span>Total Referrals</span>
              <FileText className="h-4 w-4 text-gray-400" />
            </CardDescription>
            <CardTitle className="text-3xl text-[#0A1628]">{stats?.total_referrals || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-500 font-medium">+{stats?.monthly_growth || 0}%</span>
              <span className="text-gray-500 ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center justify-between">
              <span>Pending</span>
              <Clock className="h-4 w-4 text-gray-400" />
            </CardDescription>
            <CardTitle className="text-3xl text-[#0A1628]">{stats?.pending_referrals || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="link"
              className="p-0 h-auto text-[#D4AF37] hover:text-[#C19B2B]"
              onClick={() => navigate('/referrals?status=pending')}
            >
              View pending <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center justify-between">
              <span>Scheduled</span>
              <Calendar className="h-4 w-4 text-gray-400" />
            </CardDescription>
            <CardTitle className="text-3xl text-[#0A1628]">{stats?.scheduled_referrals || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="link"
              className="p-0 h-auto text-[#D4AF37] hover:text-[#C19B2B]"
              onClick={() => navigate('/referrals?status=scheduled')}
            >
              View scheduled <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center justify-between">
              <span>Completed</span>
              <CheckCircle className="h-4 w-4 text-gray-400" />
            </CardDescription>
            <CardTitle className="text-3xl text-[#0A1628]">{stats?.completed_referrals || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-500">Success rate: 95%</div>
          </CardContent>
        </Card>
      </div>

      {/* Real-time Update Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Now
          </Button>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="autoRefresh" className="text-sm text-gray-600">
              Auto-refresh (30s)
            </label>
          </div>
          <span className="text-xs text-gray-500">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToCSV('userGrowth')}
          >
            <Download className="h-4 w-4 mr-2" />
            Export User Growth
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToCSV('referralTrends')}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Referral Trends
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToCSV('allReferrals')}
          >
            <Download className="h-4 w-4 mr-2" />
            Export All Data
          </Button>
        </div>
      </div>

      {/* Interactive Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* User Growth Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2 text-[#D4AF37]" />
              User Growth Trends
            </CardTitle>
            <CardDescription>Monthly user acquisition and total users</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  stroke="#888"
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="users" 
                  stackId="1"
                  stroke="#0A1628" 
                  fill="#0A1628" 
                  fillOpacity={0.6}
                  name="Total Users"
                />
                <Area 
                  type="monotone" 
                  dataKey="newUsers" 
                  stackId="2"
                  stroke="#D4AF37" 
                  fill="#D4AF37" 
                  fillOpacity={0.6}
                  name="New Users"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Referral Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-[#D4AF37]" />
              Referral Trends
            </CardTitle>
            <CardDescription>Weekly referral status breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={referralTrendsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="week" 
                  tick={{ fontSize: 12 }}
                  stroke="#888"
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#0A1628" 
                  strokeWidth={3}
                  dot={{ fill: '#0A1628', r: 4 }}
                  name="Total"
                />
                <Line 
                  type="monotone" 
                  dataKey="completed" 
                  stroke="#22c55e" 
                  strokeWidth={2}
                  dot={{ fill: '#22c55e', r: 4 }}
                  name="Completed"
                />
                <Line 
                  type="monotone" 
                  dataKey="scheduled" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  name="Scheduled"
                />
                <Line 
                  type="monotone" 
                  dataKey="pending" 
                  stroke="#eab308" 
                  strokeWidth={2}
                  dot={{ fill: '#eab308', r: 4 }}
                  name="Pending"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Comparison Bar Chart */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2 text-[#D4AF37]" />
            Weekly Referral Volume
          </CardTitle>
          <CardDescription>Compare referral volumes across weeks</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={referralTrendsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="week" 
                tick={{ fontSize: 12 }}
                stroke="#888"
              />
              <YAxis tick={{ fontSize: 12 }} stroke="#888" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="pending" fill="#eab308" name="Pending" />
              <Bar dataKey="scheduled" fill="#3b82f6" name="Scheduled" />
              <Bar dataKey="completed" fill="#22c55e" name="Completed" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recent Activity and Top Doctors */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Referrals</span>
              <Button variant="outline" size="sm" onClick={() => navigate('/referrals')}>
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(referrals || []).slice(0, 4).map((referral) => (
                <div key={referral.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-[#0A1628]">{referral.patient_name || referral.patientName}</span>
                      <Badge className={getStatusColor(referral.status)}>
                        {referral.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{referral.procedure}</p>
                    <p className="text-xs text-gray-500">Referred by {referral.referring_doctor_name || referral.referringDoctor}</p>
                  </div>
                  <Badge className={getUrgencyColor(referral.urgency)}>
                    {referral.urgency}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Top Referring Doctors</span>
              <Button variant="outline" size="sm" onClick={() => navigate('/doctors')}>
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(referrals || []).length > 0 ? (
                (referrals || []).slice(0, 3).map((referral) => (
                  <div key={referral.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0A1628] rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#0A1628]">{referral.referring_doctor_name}</p>
                        <p className="text-sm text-gray-600">{referral.referring_practice}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#D4AF37]">Active</p>
                      <p className="text-xs text-gray-500">referral</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No referring doctors yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <Button
              onClick={() => navigate('/referrals/new')}
              className="h-auto py-6 bg-[#0A1628] text-[#D4AF37] hover:bg-[#1a2332]"
            >
              <div className="text-center">
                <FileText className="h-6 w-6 mx-auto mb-2" />
                <div>Create New Referral</div>
              </div>
            </Button>
            <Button
              onClick={() => navigate('/doctors')}
              variant="outline"
              className="h-auto py-6"
            >
              <div className="text-center">
                <Users className="h-6 w-6 mx-auto mb-2" />
                <div>Manage Doctors</div>
              </div>
            </Button>
            <Button
              onClick={() => navigate('/patients')}
              variant="outline"
              className="h-auto py-6"
            >
              <div className="text-center">
                <Activity className="h-6 w-6 mx-auto mb-2" />
                <div>View Patients</div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const renderDoctorDashboard = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-l-4 border-l-[#D4AF37]">
          <CardHeader>
            <CardDescription>My Referrals</CardDescription>
            <CardTitle className="text-3xl">24</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-500">Total submitted</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardHeader>
            <CardDescription>Pending Response</CardDescription>
            <CardTitle className="text-3xl">5</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-500">Awaiting review</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader>
            <CardDescription>Completed</CardDescription>
            <CardTitle className="text-3xl">18</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-500">Successfully processed</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Referrals</CardTitle>
            <CardDescription>Your latest submitted referrals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(referrals || []).slice(0, 4).map((referral) => (
                <div key={referral.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{referral.patient_name}</span>
                    <Badge className={getStatusColor(referral.status)}>
                      {referral.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{referral.procedure}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={() => navigate('/referrals/new')}
              className="w-full bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            >
              <FileText className="mr-2 h-4 w-4" />
              Submit New Referral
            </Button>
            <Button
              onClick={() => navigate('/referrals')}
              variant="outline"
              className="w-full"
            >
              View All My Referrals
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );

  const renderPatientDashboard = () => (
    <>
      <Card className="mb-8 bg-gradient-to-r from-[#0A1628] to-[#1a2332] text-white">
        <CardHeader>
          <CardTitle className="text-2xl">Welcome, {user.name}!</CardTitle>
          <CardDescription className="text-gray-300">
            View your referral status and upcoming appointments
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>My Referrals</span>
              <AlertCircle className="h-5 w-5 text-[#D4AF37]" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {referrals.length > 0 ? referrals.map((referral) => (
                <div key={referral.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{referral.procedure}</span>
                    <Badge className={getStatusColor(referral.status)}>
                      {referral.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">Referred by {referral.referring_doctor_name}</p>
                  {referral.scheduled_date && (
                    <p className="text-sm text-[#D4AF37] mt-2">
                      Scheduled: {new Date(referral.scheduled_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )) : (
                <p className="text-gray-500 text-center py-4">No referrals yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={() => navigate('/referrals')}
              variant="outline"
              className="w-full justify-start"
            >
              <FileText className="mr-2 h-4 w-4" />
              View All Referrals
            </Button>
            <Button
              onClick={() => navigate('/documents')}
              variant="outline"
              className="w-full justify-start"
            >
              <FileText className="mr-2 h-4 w-4" />
              My Documents
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A1628] mb-2">
          {(effectiveRole === 'admin' || effectiveRole === 'superadmin') && 'Admin Dashboard'}
          {effectiveRole === 'referring_doctor' && 'Doctor Dashboard'}
          {effectiveRole === 'patient' && 'Patient Dashboard'}
        </h1>
        <p className="text-gray-600">
          Welcome back, {user?.name}! Here's your overview.
        </p>
      </div>

      {(effectiveRole === 'admin' || effectiveRole === 'superadmin') && renderAdminDashboard()}
      {effectiveRole === 'referring_doctor' && renderDoctorDashboard()}
      {effectiveRole === 'patient' && renderPatientDashboard()}
    </div>
  );
};

export default Dashboard;
